export const SURROUNDING_BRANCHES = [
  { id: 'garden', label: 'Plants & garden', shortLabel: 'Plants', description: 'Living things, outdoor spaces, care, and the attention they ask for.', icon: 'leaf' },
  { id: 'library', label: 'Reads & library', shortLabel: 'Reads', description: 'Books and media with a place in your life, whether read, reading, unread, or decorative.', icon: 'book' },
  { id: 'storage', label: 'Storage', shortLabel: 'Storage', description: 'Long-term, short-term, and seasonal records for what is kept out of sight.', icon: 'archive' },
  { id: 'pantry', label: 'Pantry & groceries', shortLabel: 'Pantry', description: 'Dry goods, milk, groceries, opened items, and what wants using soon.', icon: 'package' },
  { id: 'care', label: 'Care & instructions', shortLabel: 'Care', description: 'Manuals, labels, warranties, and the instructions that keep things useful.', icon: 'file' },
  { id: 'objects', label: 'Objects & decor', shortLabel: 'Objects', description: 'Furniture, art, tools, and the beautiful or practical things around you.', icon: 'box' },
];

export const SURROUNDING_BRANCH_BY_ID = Object.fromEntries(SURROUNDING_BRANCHES.map(branch => [branch.id, branch]));

export const SURROUNDING_THEMES = [
  { id: 'plants', label: 'Plants', branch: 'garden', color: '#78c88d', slogan: 'Keep something living close.' },
  { id: 'rooms', label: 'Rooms', branch: 'objects', color: '#8fb8ef', slogan: 'Make the space work for you.' },
  { id: 'books', label: 'Books', branch: 'library', color: '#d8a16d', slogan: 'Keep the next good thought within reach.' },
  { id: 'storage', label: 'Storage', branch: 'storage', color: '#b7a6df', slogan: 'Know what is kept, and why.' },
  { id: 'pantry', label: 'Pantry', branch: 'pantry', color: '#e6c66c', slogan: 'Use what you have with intention.' },
  { id: 'care', label: 'Care', branch: 'care', color: '#e58f9d', slogan: 'Small maintenance keeps life bright.' },
];

export const SURROUNDING_THEME_BY_ID = Object.fromEntries(SURROUNDING_THEMES.map(theme => [theme.id, theme]));

export const SURROUNDING_KIND_OPTIONS = {
  garden: ['Houseplant', 'Outdoor plant', 'Tree', 'Garden bed', 'Tool', 'Outdoor object'],
  library: ['Book', 'Magazine', 'Record', 'Film', 'Decorative book', 'Media'],
  storage: ['Drawer', 'Bin', 'Box', 'Closet shelf', 'Long-term storage', 'Seasonal storage'],
  pantry: ['Cereal', 'Dry good', 'Milk', 'Produce', 'Drink', 'Ingredient', 'Other grocery'],
  care: ['Manual', 'Warranty', 'Care label', 'Receipt', 'Instruction', 'Reference'],
  objects: ['Furniture', 'Art', 'Decor', 'Tool', 'Appliance', 'Keepsake', 'Object'],
};

export const SURROUNDING_DEFAULTS = {
  branch: 'objects',
  kind: 'Object',
  status: 'active',
  attentionLevel: 'none',
  tracked: false,
  trackingEveryDays: '',
  trackedAt: '',
  nextAction: '',
  nextActionAt: '',
  notes: '',
  infoText: '',
  labelText: '',
  lastWateredAt: '',
  wateringEveryDays: '',
  readingStatus: 'unread',
  storageTerm: 'short',
  storageSeason: '',
  reviewAt: '',
  expiryDate: '',
  openedAt: '',
  species: '',
  lightNeeds: '',
  marketValue: '',
  suggestedLocation: '',
  author: '',
  isbn: '',
  currentPage: '',
  totalPages: '',
  currentChapter: '',
  readingNotes: '',
  serviceSource: '',
  storageArea: '',
  boxCode: '',
  pantryZone: '',
  quantity: '',
  unit: '',
  minimumStock: '',
  careCategory: '',
  medicationName: '',
  labelVerified: false,
  instructions: '',
  objectDisposition: '',
  shareMode: 'private',
};

export function normalizeSurroundingDetails(value = {}) {
  const safe = value && typeof value === 'object' ? value : {};
  const branch = SURROUNDING_BRANCH_BY_ID[safe.branch] ? safe.branch : 'objects';
  return {
    ...SURROUNDING_DEFAULTS,
    ...safe,
    branch,
    kind: String(safe.kind || SURROUNDING_DEFAULTS.kind).trim(),
    status: String(safe.status || SURROUNDING_DEFAULTS.status).trim(),
    attentionLevel: ['none', 'soon', 'attention'].includes(safe.attentionLevel) ? safe.attentionLevel : 'none',
    tracked: !!safe.tracked,
    trackingEveryDays: String(safe.trackingEveryDays || '').trim(),
    trackedAt: String(safe.trackedAt || '').trim(),
    nextAction: String(safe.nextAction || '').trim(),
    nextActionAt: String(safe.nextActionAt || '').trim(),
    notes: String(safe.notes || '').trim(),
    infoText: String(safe.infoText || '').trim(),
    labelText: String(safe.labelText || '').trim(),
    lastWateredAt: String(safe.lastWateredAt || '').trim(),
    wateringEveryDays: String(safe.wateringEveryDays || '').trim(),
    readingStatus: ['unread', 'reading', 'read', 'shelved', 'decor', 'loaned'].includes(safe.readingStatus) ? safe.readingStatus : 'unread',
    storageTerm: ['short', 'long', 'seasonal'].includes(safe.storageTerm) ? safe.storageTerm : 'short',
    storageSeason: String(safe.storageSeason || '').trim(),
    reviewAt: String(safe.reviewAt || '').trim(),
    expiryDate: String(safe.expiryDate || '').trim(),
    openedAt: String(safe.openedAt || '').trim(),
    species: String(safe.species || '').trim(),
    lightNeeds: String(safe.lightNeeds || '').trim(),
    marketValue: String(safe.marketValue || '').trim(),
    suggestedLocation: String(safe.suggestedLocation || '').trim(),
    author: String(safe.author || '').trim(),
    isbn: String(safe.isbn || '').trim(),
    currentPage: String(safe.currentPage || '').trim(),
    totalPages: String(safe.totalPages || '').trim(),
    currentChapter: String(safe.currentChapter || '').trim(),
    readingNotes: String(safe.readingNotes || '').trim(),
    serviceSource: String(safe.serviceSource || '').trim(),
    storageArea: String(safe.storageArea || '').trim(),
    boxCode: String(safe.boxCode || '').trim(),
    pantryZone: String(safe.pantryZone || '').trim(),
    quantity: String(safe.quantity || '').trim(),
    unit: String(safe.unit || '').trim(),
    minimumStock: String(safe.minimumStock || '').trim(),
    careCategory: String(safe.careCategory || '').trim(),
    medicationName: String(safe.medicationName || '').trim(),
    labelVerified: !!safe.labelVerified,
    instructions: String(safe.instructions || '').trim(),
    objectDisposition: String(safe.objectDisposition || '').trim(),
    shareMode: ['private', 'share', 'show', 'sell'].includes(safe.shareMode) ? safe.shareMode : 'private',
  };
}

export function getSurroundingBranch(item) {
  const detail = normalizeSurroundingDetails(item?.surrounding);
  if (item?.location?.type !== 'surrounding' && !item?.surrounding) return null;
  return SURROUNDING_BRANCH_BY_ID[detail.branch] || SURROUNDING_BRANCH_BY_ID.objects;
}

export function getSurroundingKind(item) {
  const detail = normalizeSurroundingDetails(item?.surrounding);
  return detail.kind || item?.details?.category || item?.tags?.[0] || 'Object';
}

export function getSurroundingAttention(item, now = new Date()) {
  const detail = normalizeSurroundingDetails(item?.surrounding);
  const branch = getSurroundingBranch(item)?.id;
  if (!branch) return null;
  if (detail.attentionLevel === 'attention') return { level: 'attention', label: detail.nextAction || 'Needs attention' };
  if (detail.attentionLevel === 'soon') return { level: 'soon', label: detail.nextAction || 'Coming up soon' };

  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const dateFrom = value => {
    const parsed = value ? new Date(`${value}T00:00:00`) : null;
    return parsed && Number.isFinite(parsed.getTime()) ? parsed : null;
  };
  if (branch === 'garden' && detail.lastWateredAt && Number(detail.wateringEveryDays) > 0) {
    const next = dateFrom(detail.lastWateredAt);
    next?.setDate(next.getDate() + Number(detail.wateringEveryDays));
    if (next && next <= today) return { level: 'attention', label: 'Water now' };
    if (next && next.getTime() - today.getTime() <= 2 * 86400000) return { level: 'soon', label: 'Water soon' };
  }
  if (branch === 'pantry') {
    const expiry = dateFrom(detail.expiryDate);
    if (expiry && expiry <= today) return { level: 'attention', label: 'Check expiry' };
    if (expiry && expiry.getTime() - today.getTime() <= 3 * 86400000) return { level: 'soon', label: 'Use soon' };
  }
  if (branch === 'storage') {
    const review = dateFrom(detail.reviewAt);
    if (review && review <= today) return { level: 'attention', label: 'Review storage' };
    if (review && review.getTime() - today.getTime() <= 14 * 86400000) return { level: 'soon', label: 'Storage review soon' };
  }
  return null;
}

export function getSurroundingDisplayStatus(item) {
  const detail = normalizeSurroundingDetails(item?.surrounding);
  const branch = getSurroundingBranch(item)?.id;
  if (branch === 'library') return ({ unread: 'Unread', reading: 'Reading', read: 'Read', shelved: 'Shelved', decor: 'Decor', loaned: 'Loaned' })[detail.readingStatus] || 'Unread';
  if (branch === 'storage') return ({ short: 'Short-term', long: 'Long-term', seasonal: detail.storageSeason ? `Seasonal · ${detail.storageSeason}` : 'Seasonal' })[detail.storageTerm] || 'Short-term';
  if (branch === 'garden' && detail.lastWateredAt) return `Watered ${detail.lastWateredAt}`;
  if (branch === 'pantry' && detail.expiryDate) return `Use by ${detail.expiryDate}`;
  return detail.kind || 'Object';
}
